import { action, makeObservable, observable } from "mobx";
import { RootStore } from "src/stores/RootStore";
import { ICostCenter } from "./InvoiceTypes";
import { ErpType } from "src/network/User";
import { GET_COSTCENTER_DATA } from "src/api/cred";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { GetCostCenterData, GetCostCenterDataVariables } from "src/api/generated/GetCostCenterData";

export enum LOADING_COSTCENTER_DATA_STATE {
    INIT,
    LOADING
}

export class AccountingCostcenterStore {
    rootStore: RootStore;

    erpType: ErpType = ErpType.IT2;

    loadingCostcenterDataState: LOADING_COSTCENTER_DATA_STATE = LOADING_COSTCENTER_DATA_STATE.INIT;
    // Request cache for GET_COSTCENTER_DATA keyed by account id. Replaces the previous
    // "remember only the last account" check, which re-fetched whenever two rows alternated accounts
    // and handed concurrent callers the previous account's list. Not observable on purpose.
    private costcenterRequests: Map<string, Promise<ICostCenter[]>> = new Map();

    /*  These are arrays because every IT2 Accounting Row has its own costcenter value.
        The selectable costcenters are loaded per account, therefore they are stored per accounting row as well.
    */
    costcenterData: ICostCenter[][] = [];
    currentCostcenterSearchQuery: string[] = [];
    selectedCostcenterQueryString: string[] = [];
    selectedCostcenterName: string[] = [];

    triggerFocusNextInput: { isActive: boolean; index: number } = { isActive: false, index: 0 };
    triggerFocusPrevInput: { isActive: boolean; index: number } = { isActive: false, index: 0 };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            costcenterData: observable,
            currentCostcenterSearchQuery: observable,
            selectedCostcenterQueryString: observable,
            selectedCostcenterName: observable,
            triggerFocusNextInput: observable,
            triggerFocusPrevInput: observable,
            loadingCostcenterDataState: observable,

            init: action,
            reset: action,
            removeAccountingRow: action,
            loadCostcenterData: action,
            setLoadingCostcenterDataState: action,
            setCurrentCostcenterSearchQuery: action,
            setSelectedCostcenterQueryString: action,
            setSelectedCostcenterName: action,
            setTriggerFocusNextInput: action,
            setTriggerFocusPrevInput: action,
            filteredCostcenterData: action,
            setCostcenterData: action
        });
    }

    init = () => {};

    reset = () => {
        this.costcenterData = [];
        this.currentCostcenterSearchQuery = [];
        this.selectedCostcenterQueryString = [];
        this.selectedCostcenterName = [];
    };

    removeAccountingRow = (accountingRowIndex: number) => {
        this.costcenterData.splice(accountingRowIndex, 1);
        this.currentCostcenterSearchQuery.splice(accountingRowIndex, 1);
        this.selectedCostcenterQueryString.splice(accountingRowIndex, 1);
        this.selectedCostcenterName.splice(accountingRowIndex, 1);
    };

    loadCostcenterData = async (accountingRowIndex: number): Promise<ICostCenter[] | undefined> => {
        this.setLoadingCostcenterDataState(LOADING_COSTCENTER_DATA_STATE.LOADING);

        let costCenterData: ICostCenter[] = [];

        const currentInvoice = this.rootStore.invoiceStore.currentInvoice;
        const accountId = currentInvoice?.getAccountingAccount(accountingRowIndex)?.id;

        try {
            if (accountId) {
                costCenterData = await this.fetchCostcenterData(accountId);

                // Every row keeps the list of its own account, so it has to be stored on the row
                // on every call - also when the data came from the cache.
                this.setCostcenterData(costCenterData, accountingRowIndex);
            } else {
                costCenterData = this.getCostcenterData(accountingRowIndex);
            }
        } finally {
            this.setLoadingCostcenterDataState(LOADING_COSTCENTER_DATA_STATE.INIT);
        }

        return costCenterData;
    };

    private fetchCostcenterData = (accountId: string): Promise<ICostCenter[]> => {
        const pendingRequest = this.costcenterRequests.get(accountId);
        if (pendingRequest) {
            return pendingRequest;
        }

        const request = apolloClientInstance
            .query<GetCostCenterData, GetCostCenterDataVariables>({
                query: GET_COSTCENTER_DATA,
                variables: {
                    accountid: accountId
                }
            })
            .then(({ data }) =>
                data.accountcostcenters && data.accountcostcenters.length > 0
                    ? data.accountcostcenters.map((accountCostcenter) => {
                          const costCenter = accountCostcenter.costcenter;

                          return {
                              id: costCenter?.id,
                              short: costCenter?.short,
                              name: costCenter?.name
                          };
                      })
                    : []
            )
            .catch((error) => {
                // Do not cache failures - the next caller gets a fresh attempt.
                this.costcenterRequests.delete(accountId);
                throw error;
            });

        this.costcenterRequests.set(accountId, request);

        return request;
    };

    // Getters
    getFormattedCostcenterFieldValue = (costCenter: ICostCenter): string => {
        return `${costCenter.short}, ${costCenter.name}`;
    };

    getCostcenterData(accountingRowIndex: number): ICostCenter[] {
        return this.costcenterData[accountingRowIndex] ?? [];
    }

    getCurrentCostcenterSearchQuery(accountingRowIndex: number) {
        return this.currentCostcenterSearchQuery[accountingRowIndex];
    }

    getSelectedCostcenterQueryString(accountingRowIndex: number) {
        return this.selectedCostcenterQueryString[accountingRowIndex];
    }

    getSelectedCostcenterName(accountingRowIndex: number) {
        return this.selectedCostcenterName[accountingRowIndex];
    }

    // Setters
    setLoadingCostcenterDataState(loadingCostcenterDataState: LOADING_COSTCENTER_DATA_STATE) {
        this.loadingCostcenterDataState = loadingCostcenterDataState;
    }

    setCostcenterData(data: ICostCenter[], accountingRowIndex: number) {
        this.costcenterData[accountingRowIndex] = data;
    }

    setCurrentCostcenterSearchQuery(currentCostcenterSearchQuery: string, accountingRowIndex: number) {
        this.currentCostcenterSearchQuery[accountingRowIndex] = currentCostcenterSearchQuery;
    }

    setSelectedCostcenterQueryString(selectedCostcenterQueryString: string, accountingRowIndex: number) {
        this.selectedCostcenterQueryString[accountingRowIndex] = selectedCostcenterQueryString;
    }

    setSelectedCostcenterName(selectedCostcenterName: string, accountingRowIndex: number) {
        this.selectedCostcenterName[accountingRowIndex] = selectedCostcenterName;
    }

    /*  This function triggers the focus on the next field after selecting an account
        For IT2 this will be the BookingText field in the current accounting row
        For RIMO this field will not be displayed in the first place
    */
    setTriggerFocusNextInput(triggerFocusNextInput: boolean, accountingRowIndex: number) {
        this.triggerFocusNextInput = {
            isActive: triggerFocusNextInput,
            index: accountingRowIndex
        };
    }

    setTriggerFocusPrevInput(triggerFocusPrevInput: boolean, accountingRowIndex: number) {
        this.triggerFocusPrevInput = {
            isActive: triggerFocusPrevInput,
            index: accountingRowIndex
        };
    }

    filteredCostcenterData(accountingRowIndex: number): ICostCenter[] {
        const costcenterData = this.getCostcenterData(accountingRowIndex);
        let currentCostcenterSearchQuery = this.currentCostcenterSearchQuery[accountingRowIndex] ?? "";
        let selectedCostcenterQueryString = this.selectedCostcenterQueryString[accountingRowIndex] ?? "";

        if (currentCostcenterSearchQuery === selectedCostcenterQueryString) {
            return costcenterData;
        }

        if (currentCostcenterSearchQuery === "") {
            return costcenterData;
        }

        const filteredAccountData = costcenterData.filter((costCenter) => {
            const constcenterName = this.getFormattedCostcenterFieldValue(costCenter);

            return constcenterName.toLowerCase().includes(currentCostcenterSearchQuery.toLowerCase());
        });

        return filteredAccountData;
    }
}
